import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  Alert,
  Snackbar,
  Tooltip,
  Badge,
  FormGroup
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AdminPanelSettings,
  Save,
  Search,
  FilterList,
  Refresh
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { User, UserRole, Permission } from '../types';
import { 
  permissionDefinitions, 
  getPermissionsByCategory, 
  getDefaultPermissionsForRole,
  getPermissionDefinition 
} from '../services/permissionService';
import dataConnectionService from '../services/dataConnectionService';

interface UserWithRoles extends User {
  isActive: boolean;
  lastLogin: Date;
  createdAt: Date;
  updatedAt: Date;
  assignedRoles: UserRole[];
  customPermissions: Permission[]; // Özel yetkiler
}

const UserManagement: React.FC = () => {
  const { user: currentUser, hasRole } = useAuth();
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithRoles[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });

  // Yeni kullanıcı formu
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    assignedRoles: [] as UserRole[]
  });

  // Data connection durumunu kontrol et
  const isDataConnected = dataConnectionService.isConnected();
  
  // Mock kullanıcı verileri (sadece data bağlantısı yoksa)
  useEffect(() => {
    if (isDataConnected) {
      // Gerçek data'dan kullanıcıları yükle
      setUsers([]);
      return;
    }
    
    const mockUsers: UserWithRoles[] = [
      {
        id: '1',
        firstName: 'Ahmet',
        lastName: 'Yılmaz',
        email: 'ahmet@example.com',
        username: 'ahmet.yilmaz',
        role: UserRole.ADMIN,
        isActive: true,
        lastLogin: new Date(),
        createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(),
        assignedRoles: [UserRole.ADMIN, UserRole.DEVELOPER],
        customPermissions: [],
        permissions: Object.values(Permission) // Admin tüm yetkilere sahip
      },
      {
        id: '2',
        firstName: 'Fatma',
        lastName: 'Demir',
        email: 'fatma@example.com',
        username: 'fatma.demir',
        role: UserRole.CALL_ADMIN,
        isActive: true,
        lastLogin: new Date(Date.now() - 2 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(),
        assignedRoles: [UserRole.CALL_ADMIN, UserRole.CALL],
        customPermissions: [Permission.CHAT_CREATE_GROUPS], // Call Admin'e chat grup oluşturma yetkisi
        permissions: [
          Permission.CALL_REPORTS,
          Permission.CALL_MANAGEMENT,
          Permission.CALL_ANALYTICS,
          Permission.CHAT_SEND_MESSAGES,
          Permission.DAILY_REPORTS,
          Permission.WORK_TIME_TRACKING,
          Permission.WORK_TIME_VIEW,
          Permission.CHAT_CREATE_GROUPS
        ]
      },
      {
        id: '3',
        firstName: 'Mehmet',
        lastName: 'Can',
        email: 'mehmet@example.com',
        username: 'mehmet.can',
        role: UserRole.CALL,
        isActive: true,
        lastLogin: new Date(Date.now() - 5 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(),
        assignedRoles: [UserRole.CALL],
        customPermissions: [],
        permissions: [
          Permission.CALL_REPORTS,
          Permission.CHAT_SEND_MESSAGES,
          Permission.DAILY_REPORTS,
          Permission.WORK_TIME_TRACKING,
          Permission.WORK_TIME_VIEW
        ]
      },
      {
        id: '4',
        firstName: 'Ayşe',
        lastName: 'Kaya',
        email: 'ayse@example.com',
        username: 'ayse.kaya',
        role: UserRole.LIVECHAT_ADMIN,
        isActive: true,
        lastLogin: new Date(Date.now() - 1 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(),
        assignedRoles: [UserRole.LIVECHAT_ADMIN, UserRole.LIVECHAT],
        customPermissions: [Permission.USER_MANAGEMENT], // LiveChat Admin'e kullanıcı yönetimi yetkisi
        permissions: [
          Permission.LIVECHAT_REPORTS,
          Permission.LIVECHAT_MANAGEMENT,
          Permission.LIVECHAT_ANALYTICS,
          Permission.CHAT_SEND_MESSAGES,
          Permission.DAILY_REPORTS,
          Permission.WORK_TIME_TRACKING,
          Permission.WORK_TIME_VIEW,
          Permission.USER_MANAGEMENT
        ]
      },
      {
        id: '5',
        firstName: 'Ali',
        lastName: 'Veli',
        email: 'ali@example.com',
        username: 'ali.veli',
        role: UserRole.LIVECHAT,
        isActive: false,
        lastLogin: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(),
        assignedRoles: [UserRole.LIVECHAT],
        customPermissions: [],
        permissions: [
          Permission.LIVECHAT_REPORTS,
          Permission.CHAT_SEND_MESSAGES,
          Permission.DAILY_REPORTS,
          Permission.WORK_TIME_TRACKING,
          Permission.WORK_TIME_VIEW
        ]
      },
      {
        id: '6',
        firstName: 'Zeynep',
        lastName: 'Ak',
        email: 'zeynep@example.com',
        username: 'zeynep.ak',
        role: UserRole.RISK_ADMIN,
        isActive: true,
        lastLogin: new Date(Date.now() - 30 * 60 * 1000),
        createdAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(),
        assignedRoles: [UserRole.RISK_ADMIN, UserRole.RISK],
        customPermissions: [Permission.CHAT_MANAGE_GROUPS], // Risk Admin'e chat grup yönetimi yetkisi
        permissions: [
          Permission.RISK_REPORTS,
          Permission.RISK_MANAGEMENT,
          Permission.RISK_ANALYTICS,
          Permission.CHAT_SEND_MESSAGES,
          Permission.HOURLY_REPORTS,
          Permission.WORK_SUMMARY_REPORTS,
          Permission.WORK_TIME_TRACKING,
          Permission.WORK_TIME_VIEW,
          Permission.CHAT_MANAGE_GROUPS
        ]
      },
      {
        id: '7',
        firstName: 'Can',
        lastName: 'Yılmaz',
        email: 'can@example.com',
        username: 'can.yilmaz',
        role: UserRole.RISK,
        isActive: true,
        lastLogin: new Date(Date.now() - 15 * 60 * 1000),
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(),
        assignedRoles: [UserRole.RISK],
        customPermissions: [],
        permissions: [
          Permission.RISK_REPORTS,
          Permission.CHAT_SEND_MESSAGES,
          Permission.HOURLY_REPORTS,
          Permission.WORK_SUMMARY_REPORTS,
          Permission.WORK_TIME_TRACKING,
          Permission.WORK_TIME_VIEW
        ]
      }
    ];
    
    setUsers(mockUsers);
    setFilteredUsers(mockUsers);
  }, [isDataConnected]);

  // Arama fonksiyonu
  useEffect(() => {
    const filtered = users.filter(user => 
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  // Rol kontrolü - sadece Admin ve Developer erişebilir
  if (!hasRole(['admin', 'developer'])) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          Bu sayfaya erişim yetkiniz bulunmamaktadır. Sadece Admin ve Developer kullanıcıları bu sayfayı görüntüleyebilir.
        </Alert>
      </Container>
    );
  }

  const getRoleDisplayName = (role: UserRole): string => {
    const roleNames = {
      [UserRole.ADMIN]: 'Admin',
      [UserRole.DEVELOPER]: 'Developer',
      [UserRole.MOD]: 'Mod',
      [UserRole.CALL_ADMIN]: 'Call Admin',
      [UserRole.CALL]: 'Call',
      [UserRole.LIVECHAT_ADMIN]: 'Canlı Chat Admin',
      [UserRole.LIVECHAT]: 'Canlı Chat',
      [UserRole.RISK_ADMIN]: 'Risk Admin',
      [UserRole.RISK]: 'Risk'
    };
    return roleNames[role] || role;
  };

  const getRoleColor = (role: UserRole) => {
    const colors = {
      [UserRole.ADMIN]: 'error',
      [UserRole.DEVELOPER]: 'primary',
      [UserRole.MOD]: 'secondary',
      [UserRole.CALL_ADMIN]: 'warning',
      [UserRole.CALL]: 'info',
      [UserRole.LIVECHAT_ADMIN]: 'warning',
      [UserRole.LIVECHAT]: 'info',
      [UserRole.RISK_ADMIN]: 'warning',
      [UserRole.RISK]: 'info'
    };
    return colors[role] as any;
  };

  const handleUserClick = (user: UserWithRoles) => {
    setSelectedUser(user);
    setIsRoleDialogOpen(true);
  };

  const handlePermissionClick = (user: UserWithRoles) => {
    setSelectedUser(user);
    setIsPermissionDialogOpen(true);
  };

  const handleRoleChange = (role: UserRole, checked: boolean) => {
    if (!selectedUser) return;

    const updatedRoles = checked
      ? [...selectedUser.assignedRoles, role]
      : selectedUser.assignedRoles.filter(r => r !== role);

    setSelectedUser({ ...selectedUser, assignedRoles: updatedRoles });
  };

  const handleSaveRoles = () => {
    if (!selectedUser) return;

    setUsers(prev => prev.map(user => 
      user.id === selectedUser.id 
        ? { ...user, assignedRoles: selectedUser.assignedRoles }
        : user
    ));

    setSnackbar({
      open: true,
      message: `${selectedUser.firstName} ${selectedUser.lastName} kullanıcısının rolleri güncellendi.`,
      severity: 'success'
    });

    setIsRoleDialogOpen(false);
  };

  const handlePermissionChange = (permission: Permission, checked: boolean) => {
    if (!selectedUser) return;

    const updatedPermissions = checked
      ? [...selectedUser.customPermissions, permission]
      : selectedUser.customPermissions.filter(p => p !== permission);

    setSelectedUser({ ...selectedUser, customPermissions: updatedPermissions });
  };

  const handleSavePermissions = () => {
    if (!selectedUser) return;

    setUsers(prev => prev.map(user => 
      user.id === selectedUser.id 
        ? { ...user, customPermissions: selectedUser.customPermissions }
        : user
    ));

    setSnackbar({
      open: true,
      message: `${selectedUser.firstName} ${selectedUser.lastName} kullanıcısının yetkileri güncellendi.`,
      severity: 'success'
    });

    setIsPermissionDialogOpen(false);
  };

  const handleCreateUser = () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password) {
      setSnackbar({
        open: true,
        message: 'Lütfen tüm alanları doldurun.',
        severity: 'error'
      });
      return;
    }

    const user: UserWithRoles = {
      id: Date.now().toString(),
      firstName: newUser.firstName,
      lastName: newUser.lastName,
      email: newUser.email,
      username: newUser.email.split('@')[0],
      role: newUser.assignedRoles[0] || UserRole.CALL,
      isActive: true,
      lastLogin: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      assignedRoles: newUser.assignedRoles,
      customPermissions: [],
      permissions: getDefaultPermissionsForRole(newUser.assignedRoles[0] || UserRole.CALL)
    };

    setUsers(prev => [...prev, user]);
    setNewUser({ firstName: '', lastName: '', email: '', password: '', assignedRoles: [] });
    setIsCreateUserOpen(false);

    setSnackbar({
      open: true,
      message: 'Yeni kullanıcı başarıyla oluşturuldu.',
      severity: 'success'
    });
  };


  const handleDeleteUser = (userId: string) => {
    if (userId === currentUser?.id) {
      setSnackbar({
        open: true,
        message: 'Kendi hesabınızı silemezsiniz.',
        severity: 'error'
      });
      return;
    }

    setUsers(prev => prev.filter(user => user.id !== userId));
    setSnackbar({
      open: true,
      message: 'Kullanıcı başarıyla silindi.',
      severity: 'success'
    });
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Aktif' : 'Pasif';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        p: 3,
        mb: 3,
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              👥 Kullanıcı Yönetimi
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Kullanıcıları yönetin, rollerini düzenleyin ve yetkilerini kontrol edin.
            </Typography>
            
            {/* Data Connection Durumu */}
            {!isDataConnected && (
              <Alert severity="info" sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}>
                <Typography variant="body2">
                  <strong>Mock Veri Modu:</strong> Data bağlantısı kurulmadığı için örnek kullanıcı verileri gösteriliyor. 
                  Gerçek veriler için Sayfa Ayarları'ndan data bağlantısı kurun.
                </Typography>
              </Alert>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsCreateUserOpen(true)}
            sx={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 'bold',
              px: 3,
              py: 1,
              '&:hover': {
                background: 'rgba(255,255,255,0.3)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease'
              }
            }}
          >
            Yeni Kullanıcı
          </Button>
        </Box>
      </Box>

      {/* Arama ve Filtreler */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
            gap: 2,
            alignItems: 'center'
          }}>
            <TextField
              fullWidth
              placeholder="Kullanıcı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<FilterList />}
                variant="outlined"
              >
                Filtrele
              </Button>
              <Button
                startIcon={<Refresh />}
                variant="outlined"
              >
                Yenile
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Kullanıcı Listesi */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Kullanıcılar ({filteredUsers.length})
          </Typography>
          
          <List>
            {filteredUsers.map((user) => (
              <React.Fragment key={user.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Rolleri Düzenle">
                        <IconButton
                          onClick={() => handleUserClick(user)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Yetkileri Düzenle">
                        <IconButton
                          onClick={() => handlePermissionClick(user)}
                          color="secondary"
                        >
                          <AdminPanelSettings />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Kullanıcıyı Sil">
                        <span>
                          <IconButton
                            onClick={() => handleDeleteUser(user.id)}
                            color="error"
                            disabled={user.id === currentUser?.id}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => handleUserClick(user)}>
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: getStatusColor(user.isActive),
                              border: '2px solid white'
                            }}
                          />
                        }
                      >
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {user.firstName[0]}{user.lastName[0]}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Chip
                            label={getRoleDisplayName(user.role)}
                            size="small"
                            color={getRoleColor(user.role)}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          <Chip
                            label={getStatusText(user.isActive)}
                            size="small"
                            color={getStatusColor(user.isActive)}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Son giriş: {formatDate(user.lastLogin)} • 
                            Oluşturulma: {formatDate(user.createdAt)}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {user.assignedRoles.map((role, index) => (
                              <Chip
                                key={index}
                                label={getRoleDisplayName(role)}
                                size="small"
                                variant="outlined"
                                color={getRoleColor(role)}
                                sx={{ fontSize: '0.6rem', height: 18, mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Rol Düzenleme Dialog */}
      <Dialog 
        open={isRoleDialogOpen} 
        onClose={() => setIsRoleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {selectedUser?.firstName[0]}{selectedUser?.lastName[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedUser?.firstName} {selectedUser?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.email}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Rol Yönetimi
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Kullanıcıya atamak istediğiniz rolleri seçin. Birden fazla rol seçebilirsiniz.
          </Typography>
          
          <FormGroup>
            {Object.values(UserRole).map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    checked={selectedUser?.assignedRoles.includes(role) || false}
                    onChange={(e) => handleRoleChange(role, e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={getRoleDisplayName(role)}
                      size="small"
                      color={getRoleColor(role)}
                      sx={{ fontSize: '0.8rem' }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {role === UserRole.ADMIN && 'Tüm yetkilere sahip'}
                      {role === UserRole.DEVELOPER && 'Sayfa düzenleme ve CSS'}
                      {role === UserRole.MOD && 'Developer alt yetkisi'}
                      {role === UserRole.CALL_ADMIN && 'Call ekibi yönetimi'}
                      {role === UserRole.CALL && 'Call işlemleri'}
                      {role === UserRole.LIVECHAT_ADMIN && 'LiveChat ekibi yönetimi'}
                      {role === UserRole.LIVECHAT && 'LiveChat işlemleri'}
                      {role === UserRole.RISK_ADMIN && 'Risk ekibi yönetimi'}
                      {role === UserRole.RISK && 'Risk işlemleri'}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setIsRoleDialogOpen(false)}>
            İptal
          </Button>
          <Button
            onClick={handleSaveRoles}
            variant="contained"
            startIcon={<Save />}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detaylı Yetki Yönetimi Dialog */}
      <Dialog 
        open={isPermissionDialogOpen} 
        onClose={() => setIsPermissionDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              <AdminPanelSettings />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedUser?.firstName} {selectedUser?.lastName} - Yetki Yönetimi
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.email}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Detaylı Yetki Yönetimi
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Kullanıcıya özel yetkiler atayabilir veya kaldırabilirsiniz. Bu yetkiler rol yetkilerine ek olarak çalışır.
          </Typography>
          
          {Object.entries(getPermissionsByCategory(permissionDefinitions.map(p => p.id))).map(([category, permissions]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                {category}
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2 
              }}>
                {permissions.map((permission) => {
                  const definition = getPermissionDefinition(permission);
                  if (!definition) return null;
                  
                  const isChecked = selectedUser?.customPermissions.includes(permission) || false;
                  
                  return (
                    <Card 
                      key={permission} 
                      sx={{ 
                        p: 2, 
                        border: isChecked ? '2px solid' : '1px solid',
                        borderColor: isChecked ? 'primary.main' : 'divider',
                        bgcolor: isChecked ? 'primary.50' : 'background.paper',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {definition.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {definition.description}
                            </Typography>
                            {definition.isDangerous && (
                              <Chip 
                                label="Tehlikeli" 
                                size="small" 
                                color="error" 
                                sx={{ ml: 1, fontSize: '0.6rem' }}
                              />
                            )}
                          </Box>
                        }
                        sx={{ width: '100%', alignItems: 'flex-start' }}
                      />
                    </Card>
                  );
                })}
              </Box>
            </Box>
          ))}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setIsPermissionDialogOpen(false)}>
            İptal
          </Button>
          <Button
            onClick={handleSavePermissions}
            variant="contained"
            startIcon={<Save />}
            color="secondary"
          >
            Yetkileri Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Yeni Kullanıcı Oluşturma Dialog */}
      <Dialog 
        open={isCreateUserOpen} 
        onClose={() => setIsCreateUserOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Yeni Kullanıcı Oluştur
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Ad"
              value={newUser.firstName}
              onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Soyad"
              value={newUser.lastName}
              onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
            />
            <TextField
              fullWidth
              label="E-posta"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Şifre"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            />
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Roller
              </Typography>
              <FormGroup>
                {Object.values(UserRole).map((role) => (
                  <FormControlLabel
                    key={role}
                    control={
                      <Checkbox
                        checked={newUser.assignedRoles.includes(role)}
                        onChange={(e) => {
                          const updatedRoles = e.target.checked
                            ? [...newUser.assignedRoles, role]
                            : newUser.assignedRoles.filter(r => r !== role);
                          setNewUser({ ...newUser, assignedRoles: updatedRoles });
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={getRoleDisplayName(role)}
                          size="small"
                          color={getRoleColor(role)}
                          sx={{ fontSize: '0.8rem' }}
                        />
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          </Stack>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setIsCreateUserOpen(false)}>
            İptal
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            startIcon={<Add />}
          >
            Kullanıcı Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagement;
